/**
 * <tabs-accordion> — one semantic markup source that behaves as tabs on desktop and as an
 * accordion on mobile.
 *
 * Markup (rendered by blocks/tabs.liquid + blocks/_tab.liquid):
 *   <tabs-accordion>
 *     <div class="tabs__item">
 *       <button class="tabs__trigger" aria-expanded aria-controls>Title</button>
 *       <div class="tabs__panel" role="region" hidden>…</div>
 *     </div>
 *   </tabs-accordion>
 *
 * - >= 750px (Horizon's mobile breakpoint): exactly one panel is open, like tabs. Arrow / Home / End move between tabs.
 * - <  750px: every panel toggles independently, like an accordion. Nothing is forced open.
 */
class TabsAccordion extends HTMLElement {
  #media = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
    this.addEventListener('shopify:block:select', this.#onEditorSelect);
    this.#media.addEventListener('change', this.#onBreakpoint);
    this.#apply(this.#media.matches, true);
    this.dataset.ready = 'true';
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('shopify:block:select', this.#onEditorSelect);
    this.#media.removeEventListener('change', this.#onBreakpoint);
  }

  get #triggers() {
    return Array.from(this.querySelectorAll('.tabs__trigger'));
  }

  #panel(trigger) {
    return document.getElementById(trigger.getAttribute('aria-controls'));
  }

  #set(trigger, open) {
    const panel = this.#panel(trigger);
    trigger.setAttribute('aria-expanded', String(open));
    trigger.closest('.tabs__item')?.classList.toggle('is-open', open);
    if (panel) panel.hidden = !open;
  }

  /** Re-apply the right behaviour for the current viewport. */
  #apply(isDesktop, initial = false) {
    const triggers = this.#triggers;
    if (!triggers.length) return;
    const open = triggers.filter((t) => t.getAttribute('aria-expanded') === 'true');

    if (isDesktop) {
      // Tabs: keep exactly one open (first one that was open, otherwise the first tab).
      const keep = open[0] ?? triggers[0];
      triggers.forEach((t) => this.#set(t, t === keep));
      this.setAttribute('data-mode', 'tabs');
    } else {
      // Accordion: start fully collapsed on first load; when resizing from desktop keep whatever is open.
      if (initial) triggers.forEach((t) => this.#set(t, false));
      this.setAttribute('data-mode', 'accordion');
    }
  }

  #onBreakpoint = (event) => this.#apply(event.matches);

  #onClick = (event) => {
    const trigger = event.target.closest('.tabs__trigger');
    if (!trigger || !this.contains(trigger)) return;
    const isOpen = trigger.getAttribute('aria-expanded') === 'true';

    if (this.#media.matches) {
      if (!isOpen) this.#triggers.forEach((t) => this.#set(t, t === trigger));
    } else {
      this.#set(trigger, !isOpen);
    }
  };

  #onKeydown = (event) => {
    if (!this.#media.matches) return; // native button behaviour is enough for the accordion
    const trigger = event.target.closest('.tabs__trigger');
    if (!trigger) return;
    const triggers = this.#triggers;
    const i = triggers.indexOf(trigger);
    const rtl = getComputedStyle(this).direction === 'rtl';
    let next = null;
    if (event.key === 'ArrowRight') next = triggers[(i + (rtl ? -1 : 1) + triggers.length) % triggers.length];
    else if (event.key === 'ArrowLeft') next = triggers[(i + (rtl ? 1 : -1) + triggers.length) % triggers.length];
    else if (event.key === 'Home') next = triggers[0];
    else if (event.key === 'End') next = triggers[triggers.length - 1];
    if (!next) return;
    event.preventDefault();
    next.focus();
    triggers.forEach((t) => this.#set(t, t === next));
  };

  /** Theme editor: selecting a tab block in the sidebar reveals its panel. */
  #onEditorSelect = (event) => {
    const trigger = event.target.querySelector?.(':scope > .tabs__trigger');
    if (!trigger) return;
    if (this.#media.matches) this.#triggers.forEach((t) => this.#set(t, t === trigger));
    else this.#set(trigger, true);
  };
}

if (!customElements.get('tabs-accordion')) {
  customElements.define('tabs-accordion', TabsAccordion);
}
